import { useEffect, useRef, useState } from 'react'
import { envoyerMessage, initiales, marquerFilLu } from '../../api/operations'
import { totalNonLus } from '../../api/messagerieClients'
import type { DonneesOps, Livreur, Message, StatutLivreur } from '../../api/types'
import { Icone, type NomIcone } from '../Icone'
import { dateCourte, estAujourdHui, heure } from '../Format'
import { FilClients, FilCourses } from './MessagerieClients'

export type OngletMessagerie = 'clients' | 'courses' | 'livreurs'

/**
 * Messagerie du back office (0067) : trois fils, jamais mélangés.
 *  • Clients : l'assistance client ↔ équipe (le livreur ne la voit jamais) ;
 *  • Courses en cours : le fil client ↔ livreur des courses de l'app, où l'équipe peut intervenir ;
 *  • Livreurs : le fil équipe ↔ livreur.
 */
export function EcranMessagerie({
  donnees,
  orgId,
  surRafraichir,
  surNotifier,
  ongletInitial = 'clients',
  clientInitial = null,
  courseInitiale = null,
}: {
  donnees: DonneesOps
  orgId: string
  surRafraichir: () => Promise<void>
  surNotifier: (message: string) => void
  ongletInitial?: OngletMessagerie
  clientInitial?: string | null
  courseInitiale?: string | null
}) {
  const [onglet, setOnglet] = useState<OngletMessagerie>(ongletInitial)
  useEffect(() => setOnglet(ongletInitial), [ongletInitial, clientInitial])
  const nonLusClients = totalNonLus(donnees.conversationsClients)
  const nonLusLivreurs = donnees.messages.filter((m) => m.sender === 'driver' && !m.read_at).length
  const coursesApp = donnees.courses.filter((c) => c.app_particulier && ['assignee', 'collecte', 'en_livraison'].includes(c.statut)).length
  const ONGLETS: { id: OngletMessagerie; libelle: string; icone: NomIcone; compte: number }[] = [
    { id: 'clients', libelle: 'Clients', icone: 'users', compte: nonLusClients },
    { id: 'courses', libelle: 'Courses en cours', icone: 'dispatch', compte: 0 },
    { id: 'livreurs', libelle: 'Livreurs', icone: 'user', compte: nonLusLivreurs },
  ]
  return (
    <>
      <div className="subtabs" role="tablist" aria-label="Fils de discussion">
        {ONGLETS.map((o) => (
          <button key={o.id} role="tab" aria-selected={o.id === onglet} className={`subtab${o.id === onglet ? ' active' : ''}`} onClick={() => setOnglet(o.id)}>
            <Icone nom={o.icone} /> {o.libelle}
            {o.id === 'courses' && coursesApp > 0 && <span className="subtab-compte neutre">{coursesApp}</span>}
            {o.compte > 0 && <span className="subtab-compte">{o.compte}</span>}
          </button>
        ))}
      </div>
      <div className="card" data-anim style={{ marginBottom: 0 }}>
        {onglet === 'clients' ? (
          <FilClients donnees={donnees} surRafraichir={surRafraichir} surNotifier={surNotifier} clientInitial={clientInitial} courseInitiale={courseInitiale} />
        ) : onglet === 'courses' ? (
          <FilCourses donnees={donnees} surNotifier={surNotifier} />
        ) : (
          <FilLivreurs donnees={donnees} orgId={orgId} surRafraichir={surRafraichir} surNotifier={surNotifier} />
        )}
      </div>
    </>
  )
}

/**
 * Fil équipe ↔ livreurs : TRANSPOSITION de `renderMessagerie` /
 * `renderThread` / `selectThread` / `markThreadRead` / `sendDispatchMsg` /
 * `onDispatchIncomingMsg`.
 *
 * Classes REPRISES telles quelles : `.msg-cols` / `.msg-side` / `.ms-head` /
 * `.ms-item` / `.mi-*` pour la liste des livreurs, `.msg-main` / `.mt-head` /
 * `.msg-thread` / `.msg-day` / `.msg-b` / `.from-driver` / `.from-dispatch` /
 * `.msg-compose` / `.msg-send` / `.msg-placeholder` pour le fil.
 */

const LIBELLE_STATUT_LIVREUR: Record<StatutLivreur, string> = {
  available: 'Disponible',
  on_course: 'En course',
  offline: 'Hors ligne',
}

/** `msgDayLabel()` : "Aujourd'hui" / "Hier" / "Mardi 23 septembre". */
function libelleJour(iso: string): string {
  const d = new Date(iso)
  const maintenant = new Date()
  if (d.toDateString() === maintenant.toDateString()) return "Aujourd'hui"
  if (d.toDateString() === new Date(maintenant.getTime() - 86_400_000).toDateString()) return 'Hier'
  const s = d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })
  return s.charAt(0).toUpperCase() + s.slice(1)
}

function FilLivreurs({
  donnees,
  orgId,
  surRafraichir,
  surNotifier,
}: {
  donnees: DonneesOps
  orgId: string
  surRafraichir: () => Promise<void>
  surNotifier: (message: string) => void
}) {
  const [filSelectionne, setFilSelectionne] = useState<string | null>(null)
  const [brouillon, setBrouillon] = useState('')
  const filRef = useRef<HTMLDivElement>(null)

  const messagesDe = (livreurId: string): Message[] =>
    donnees.messages.filter((m) => m.driver_id === livreurId)
  const nonLusDe = (livreurId: string): number =>
    messagesDe(livreurId).filter((m) => m.sender === 'driver' && !m.read_at).length

  /**
   * `marquerFilLu()` — appelée à l'entrée sur le fil ET à chaque changement de
   * fil, comme l'original (`go('messagerie')` puis `selectThread()`). Un seul
   * effet suffit à couvrir les deux : il se déclenche au montage (entrée sur
   * l'écran) autant qu'à chaque changement de `filSelectionne`.
   */
  useEffect(() => {
    if (!filSelectionne) return
    if (nonLusDe(filSelectionne) === 0) return
    marquerFilLu(orgId, filSelectionne)
      .then(() => surRafraichir())
      .catch(() => {
        /* re-marqués au prochain passage — comme l'original. */
      })
  }, [filSelectionne, orgId])

  /**
   * `onDispatchIncomingMsg()` — diffuse les messages livreur NOUVEAUX depuis le
   * dernier rendu : marque le fil lu s'il est déjà ouvert, sinon notifie.
   *
   * ⚠️ Portée réduite par rapport à l'original : ce composant ne s'exécute que
   * lorsque l'écran Messagerie est affiché. L'original notifiait depuis
   * N'IMPORTE QUEL écran (l'abonnement temps réel était global). Reproduire
   * cela exigerait que l'abonnement `suivreMessages` de App.tsx transmette la
   * ligne insérée à un notifieur partagé — hors de portée ici, qui ne touche
   * que ce fichier.
   */
  const idsConnus = useRef<Set<string> | null>(null)
  useEffect(() => {
    if (idsConnus.current === null) {
      // Premier rendu : on mémorise l'existant sans rien notifier, sinon
      // toute la messagerie déjà lue déclencherait une salve de toasts.
      idsConnus.current = new Set(donnees.messages.map((m) => m.id))
      return
    }
    for (const m of donnees.messages) {
      if (idsConnus.current.has(m.id)) continue
      idsConnus.current.add(m.id)
      if (m.sender !== 'driver') continue
      if (m.driver_id === filSelectionne) {
        marquerFilLu(orgId, m.driver_id)
          .then(() => surRafraichir())
          .catch(() => {
            /* re-marqués au prochain passage. */
          })
        continue
      }
      const d = donnees.drivers.find((x) => x.id === m.driver_id)
      surNotifier(`Message de ${d ? d.full_name : 'livreur'} : ${m.body.length > 60 ? `${m.body.slice(0, 60)}…` : m.body}`)
    }
  }, [donnees.messages])

  async function envoyer() {
    const texte = brouillon.trim()
    if (!texte || !filSelectionne) return
    setBrouillon('')
    try {
      await envoyerMessage(orgId, filSelectionne, texte)
      await surRafraichir()
    } catch (e) {
      surNotifier(`Envoi impossible : ${e instanceof Error ? e.message : String(e)}`)
      setBrouillon(texte)
    }
  }

  // Auto-scroll en bas du fil à l'ouverture et à chaque nouveau message.
  useEffect(() => {
    const el = filRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [filSelectionne, donnees.messages])

  const lignes = donnees.drivers
    .map((d) => {
      const ms = messagesDe(d.id)
      return { livreur: d, dernier: ms[ms.length - 1] ?? null, nonLus: nonLusDe(d.id) }
    })
    .sort(
      (a, b) =>
        (b.dernier ? new Date(b.dernier.created_at).getTime() : 0) -
        (a.dernier ? new Date(a.dernier.created_at).getTime() : 0),
    )

  const livreurActif: Livreur | null = donnees.drivers.find((d) => d.id === filSelectionne) ?? null
  const messagesFil = filSelectionne ? messagesDe(filSelectionne) : []

  let dernierJour = ''
  const bulles = messagesFil.map((m) => {
    const jour = libelleJour(m.created_at)
    const nouveauJour = jour !== dernierJour
    dernierJour = jour
    return (
      <div key={m.id}>
        {nouveauJour && <div className="msg-day">{jour}</div>}
        <div className={`msg-b from-${m.sender === 'driver' ? 'driver' : 'dispatch'}`}>
          {m.body}
          <div className="mb-time">{heure(m.created_at)}</div>
        </div>
      </div>
    )
  })

  return (
      <div className="msg-cols">
        <div className="msg-side">
          <div className="ms-head">
            {donnees.drivers.length} livreur{donnees.drivers.length > 1 ? 's' : ''}
          </div>
          <div>
            {lignes.length === 0 ? (
              <div className="msg-placeholder" style={{ padding: 22 }}>
                Aucun livreur dans la flotte.
              </div>
            ) : (
              lignes.map((x, i) => (
                <div
                  key={x.livreur.id}
                  className={`ms-item${filSelectionne === x.livreur.id ? ' sel' : ''}`}
                  style={{ animationDelay: `${i * 35}ms` }}
                  onClick={() => setFilSelectionne(x.livreur.id)}
                >
                  <span className="avatar">{initiales(x.livreur.full_name)}</span>
                  <div className="mi-body">
                    <div className="mi-name">{x.livreur.full_name}</div>
                    <div className="mi-last">
                      {x.dernier
                        ? `${x.dernier.sender === 'dispatch' ? 'Vous : ' : ''}${x.dernier.body}`
                        : 'Aucun message — dites bonjour'}
                    </div>
                  </div>
                  <div className="mi-meta">
                    {x.dernier && (
                      <span className="mi-time">
                        {estAujourdHui(x.dernier.created_at)
                          ? heure(x.dernier.created_at)
                          : dateCourte(x.dernier.created_at).slice(0, 5)}
                      </span>
                    )}
                    {x.nonLus > 0 && <span className="mi-unread">{x.nonLus}</span>}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="msg-main">
          {!livreurActif ? (
            <div className="msg-placeholder">
              <Icone nom="chat" />
              <div className="mp-t">Messagerie livreurs</div>
              Sélectionnez un livreur à gauche pour ouvrir la conversation.
            </div>
          ) : (
            <>
              <div className="mt-head">
                <span className="avatar">{initiales(livreurActif.full_name)}</span>
                <div>
                  <div className="th-name">{livreurActif.full_name}</div>
                  <div className="th-sub">
                    {livreurActif.phone ? `${livreurActif.phone} · ` : ''}
                    {LIBELLE_STATUT_LIVREUR[livreurActif.status]}
                  </div>
                </div>
              </div>
              <div className="msg-thread" ref={filRef}>
                {messagesFil.length === 0 ? (
                  <div className="msg-placeholder">
                    Aucun message avec {livreurActif.full_name} — écrivez le premier.
                  </div>
                ) : (
                  bulles
                )}
              </div>
              <div className="msg-compose">
                <input
                  placeholder={`Écrire à ${livreurActif.full_name}…`}
                  maxLength={2000}
                  autoComplete="off"
                  value={brouillon}
                  onChange={(e) => setBrouillon(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') void envoyer()
                  }}
                />
                <button className="msg-send" onClick={() => void envoyer()} aria-label="Envoyer" title="Envoyer">
                  <Icone nom="send" />
                </button>
              </div>
            </>
          )}
        </div>
      </div>
  )
}
